// Provider-safe wrapper around `useGoogleReCaptcha` from
// react-google-recaptcha-v3. The upstream hook throws
// "GoogleReCaptchaProvider not found" when called outside the provider;
// our AppProviders only mounts the provider when env.recaptchaSiteKey is
// set (dev workstation without a key works without the provider). This
// wrapper inspects env.recaptchaSiteKey first and only calls the upstream
// hook when the provider is guaranteed to be mounted.
//
// Refs: docs/02-workflow/working-method.md §13 · Spec V1.1 §M13.6

import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { env } from './env';
import type { RecaptchaAction } from './recaptcha';

export type RecaptchaTokenGetter = (action: RecaptchaAction) => Promise<string | null>;

export function useRecaptchaToken(): RecaptchaTokenGetter {
  // Dev bypass branch: site key absent → Provider not mounted →
  // calling useGoogleReCaptcha would crash. Return a no-op getter that
  // emits null tokens; verifyRecaptcha then treats the call as a dev
  // bypass (success: true) and the page proceeds to the underlying
  // signIn / signUp / sendPasswordReset call.
  //
  // env.recaptchaSiteKey is computed once at module load — its value
  // never changes between renders, so the order of hook calls below
  // stays stable across the component lifecycle. This is the documented
  // exception that allows the rules-of-hooks override.
  if (!env.recaptchaSiteKey) {
    return async () => null;
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { executeRecaptcha } = useGoogleReCaptcha();
  return async (action: RecaptchaAction) => {
    if (!executeRecaptcha) return null; // script still loading
    try {
      return await executeRecaptcha(action);
    } catch {
      return null;
    }
  };
}
